import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppHeaderLayout from '../components/AppHeaderLayout';
import { useCategoriesByUserId, useGroupedCategoriesByUserId } from '../api/categories';
import { useSelector } from 'react-redux';
import Spinner from '../components/Spinner';
import { 
    useCreateCategory, 
    useUpdateCategory, 
    useDeleteCategory, 
    useUpdateCategoryGroup,
    useDeleteCategoryGroup 
} from '../api/categories';
import { Button } from '../components/ui/button';
import CreateCategoryModal from '../components/Modals/CreateCategoryModal';
import { buttonStyles, inputStyles } from '../styles/commonStyles';
import { FolderPlusIcon, Squares2X2Icon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { MoreVertical, GripVertical } from 'lucide-react';
import CategoryCard from '../components/Categories/CategoryCard';
import CreateGroupModal from '../components/Modals/CreateGroupModal';
import CategoryActionsMenu from '../components/Menus/CategoryActionsMenu';
import GroupOptionsMenu from '../components/Menus/GroupOptionsMenu';
import EditGroupModal from '../components/Modals/EditGroupModal';
import DeleteGroupModal from '../components/Modals/DeleteGroupModal';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useQueryClient } from 'react-query';

function MyCategories() {
    const { t } = useTranslation();
    const userId = useSelector(state => state.auth.user?.id);
    const { data: groupedData, isLoading } = useGroupedCategoriesByUserId(userId);
    const createCategoryMutation = useCreateCategory();
    const updateCategoryMutation = useUpdateCategory();
    const deleteCategoryMutation = useDeleteCategory();
    const updateGroupMutation = useUpdateCategoryGroup();
    const deleteGroupMutation = useDeleteCategoryGroup();
    const queryClient = useQueryClient();

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [groupMenuStates, setGroupMenuStates] = useState({});
    const [editingGroup, setEditingGroup] = useState(null);
    const [isEditGroupModalOpen, setIsEditGroupModalOpen] = useState(false);
    const [deletingGroup, setDeletingGroup] = useState(null);
    const [isDeleteGroupModalOpen, setIsDeleteGroupModalOpen] = useState(false);
    const [filter, setFilter] = useState("");

    if (!userId) return <div>{t('common.pleaseLogin')}</div>;
    if (isLoading) return <Spinner />;

    const handleEdit = (category) => {
        setEditingCategory(category);
        setIsCreateModalOpen(true);
    };

    const handleDelete = async (categoryId) => {
        try {
            await deleteCategoryMutation.mutateAsync({ id: categoryId, userId });
            // Автоматически обновляем данные после успешного удаления
            queryClient.invalidateQueries(['categories', userId]);
            queryClient.invalidateQueries(['groupedCategories', userId]);
        } catch (error) {
            // Показываем пользователю детальную информацию об ошибке
            let errorMessage = 'Ошибка при удалении категории';
            
            if (error?.detail) {
                if (typeof error.detail === 'object' && error.detail.postcard_ids) {
                    // Детальная информация от бэкенда
                    const { message, postcard_ids, total_count, showing_first } = error.detail;
                    errorMessage = `${message}\n\nID открыток: ${postcard_ids.join(', ')}\n\nПоказано первых: ${showing_first} из ${total_count}`;
                } else {
                    errorMessage = error.detail;
                }
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            alert(errorMessage);
        }
    };

    const handleMobileMenuSelect = (action) => {
        if (action === 'createGroup') {
            setIsCreateGroupModalOpen(true);
        } else if (action === 'createCategory') {
            setIsCreateModalOpen(true);
        }
    };

    const handleGroupMenuToggle = (groupId) => {
        setGroupMenuStates(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };

    const handleGroupAction = (groupId, action) => {
        const group = groupedData.groups.find(g => g.group.id === groupId)?.group;
        if (!group) return;

        if (action === "editGroup") {
            setEditingGroup(group);
            setIsEditGroupModalOpen(true);
        } else if (action === "deleteGroup") {
            setDeletingGroup(group);
            setIsDeleteGroupModalOpen(true);
        }
    };

    const handleGroupSave = async (updatedGroup) => {
        try {
            await updateGroupMutation.mutateAsync({
                groupId: updatedGroup.id,
                data: {
                    name: updatedGroup.name,
                    description: updatedGroup.description
                }
            });
        } catch (error) {
            console.error('Error updating group:', error);
            alert(t('categories.updateError', 'Ошибка при обновлении группы'));
        }
    };

    const handleGroupDelete = async () => {
        if (!deletingGroup) return;

        try {
            await deleteGroupMutation.mutateAsync(deletingGroup.id);
            setIsDeleteGroupModalOpen(false);
            setDeletingGroup(null);
        } catch (error) {
            console.error('Error deleting group:', error);
            alert(t('categories.deleteError', 'Ошибка при удалении группы'));
        }
    };

    // Фильтрация только по названию категории
    const filterText = filter.trim().toLowerCase();
    const filteredGroups = groupedData?.groups?.map(({ group, categories }) => {
        // Фильтруем только категории внутри группы
        const filteredCategories = categories.filter(cat =>
            cat.name.toLowerCase().includes(filterText)
        );
        return {
            group,
            categories: filteredCategories
        };
    }).filter(({ categories }) => categories.length > 0);

    const filteredCategoriesWithoutGroup = groupedData?.categories_without_group?.filter(cat =>
        cat.name.toLowerCase().includes(filterText)
    ) || [];

    // Функция для сортировки категорий по position, если есть, иначе по id
    function sortCategories(arr) {
        return [...arr].sort((a, b) => {
            if (a.position != null && b.position != null) return a.position - b.position;
            if (a.position != null) return -1;
            if (b.position != null) return 1;
            return a.id - b.id;
        });
    }

    // Функция для сортировки групп по position, если есть, иначе по id
    function sortGroups(arr) {
        return [...arr].sort((a, b) => {
            if (a.group.position != null && b.group.position != null) return a.group.position - b.group.position;
            if (a.group.position != null) return -1;
            if (b.group.position != null) return 1;
            return a.group.id - b.group.id;
        });
    }

    const handleDragEnd = async (result) => {
        const { source, destination, draggableId, type } = result;
        if (!destination) return;
        // Перемещение групп
        if (type === 'group') {
            const groups = sortGroups(filteredGroups);
            const [removed] = groups.splice(source.index, 1);
            groups.splice(destination.index, 0, removed);
            for (let i = 0; i < groups.length; i++) {
                if (groups[i].group.position !== i) {
                    await updateGroupMutation.mutateAsync({
                        groupId: groups[i].group.id,
                        data: { position: i }
                    });
                }
            }
            await queryClient.invalidateQueries(['groupedCategories', userId]);
            await queryClient.invalidateQueries(['category-groups', userId]);
            return;
        }
        // Перемещение категорий (старый код)
        // Получаем исходную и целевую группу
        const sourceGroup = (groupedData?.groups || []).find(g => g.group.id.toString() === source.droppableId);
        const destGroup = (groupedData?.groups || []).find(g => g.group.id.toString() === destination.droppableId);
        if (!sourceGroup || !destGroup) return;
        // Получаем массивы категорий
        const sourceCategories = sortCategories(sourceGroup.categories);
        const destCategories = sortCategories(destGroup.categories);
        // Находим категорию
        const movedCategory = sourceCategories[source.index];
        if (!movedCategory) return;
        // Если перенос внутри одной группы
        if (source.droppableId === destination.droppableId) {
            sourceCategories.splice(source.index, 1);
            sourceCategories.splice(destination.index, 0, movedCategory);
            for (let i = 0; i < sourceCategories.length; i++) {
                if (sourceCategories[i].position !== i) {
                    await updateCategoryMutation.mutateAsync({
                        id: sourceCategories[i].id,
                        position: i
                    });
                }
            }
        } else {
            // Перенос между группами
            // Удаляем из исходной группы
            sourceCategories.splice(source.index, 1);
            // Вставляем в целевую группу
            destCategories.splice(destination.index, 0, movedCategory);
            // Обновляем group_id и position для перемещённой категории
            await updateCategoryMutation.mutateAsync({
                id: movedCategory.id,
                group_id: destGroup.group.id,
                position: destination.index
            });
            // Обновляем позиции остальных категорий в целевой группе
            for (let i = 0; i < destCategories.length; i++) {
                if (destCategories[i].id !== movedCategory.id && destCategories[i].position !== i) {
                    await updateCategoryMutation.mutateAsync({
                        id: destCategories[i].id,
                        position: i
                    });
                }
            }
            // Обновляем позиции в исходной группе
            for (let i = 0; i < sourceCategories.length; i++) {
                if (sourceCategories[i].position !== i) {
                    await updateCategoryMutation.mutateAsync({
                        id: sourceCategories[i].id,
                        position: i
                    });
                }
            }
        }
        // После всех обновлений рефетчим группы
        await queryClient.invalidateQueries(['groupedCategories', userId]);
    };

    return (
        <AppHeaderLayout 
            title={t('categories.title')}
            rightContent={
                <>
                    <div className="hidden md:flex gap-2">
                        <button
                            onClick={() => setIsCreateGroupModalOpen(true)}
                            className={buttonStyles}
                        >
                            <Squares2X2Icon className="w-5 h-5 mr-2 inline-block" />
                            {t('categories.addGroup', 'Создать группу')}
                        </button>
                        <button 
                            onClick={() => setIsCreateModalOpen(true)} 
                            className={buttonStyles}
                        >
                            <FolderPlusIcon className="w-5 h-5 mr-2 inline-block" />
                            {t('categories.addNew')}
                        </button>
                    </div>
                    <div className="flex md:hidden">
                        <button
                            className={buttonStyles + ' flex items-center justify-center'}
                            onClick={() => setIsMobileMenuOpen(true)}
                            aria-label="More actions"
                        >
                            <EllipsisVerticalIcon className="w-6 h-6" />
                        </button>
                        <CategoryActionsMenu
                            isOpen={isMobileMenuOpen}
                            onClose={() => setIsMobileMenuOpen(false)}
                            onSelect={handleMobileMenuSelect}
                        />
                    </div>
                </>
            }
        >
            <div className="p-4 space-y-8">
                {/* Поле фильтрации */}
                <div className="mb-4">
                    <input
                        type="text"
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                        placeholder={t('categories.filterPlaceholder', 'Поиск по названию категории...')}
                        className={inputStyles}
                    />
                </div>
                {/* Группы категорий */}
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="all-groups" type="group">
                        {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                                {sortGroups(filteredGroups).map(({ group, categories }, groupIdx) => (
                                    <Draggable key={group.id} draggableId={"group-" + group.id} index={groupIdx}>
                                        {(groupProvided, groupSnapshot) => (
                                            <div
                                                ref={groupProvided.innerRef}
                                                {...groupProvided.draggableProps}
                                                className={groupSnapshot.isDragging ? 'opacity-80 mb-8' : 'mb-8'}
                                            >
                                                <div className="flex items-center justify-between mb-2" {...groupProvided.dragHandleProps}>
                                                    <div className="flex items-center">
                                                        <span className="cursor-move mr-2 text-gray-400 hover:text-gray-600">
                                                            <GripVertical className="w-5 h-5" />
                                                        </span>
                                                        <h2 className="text-lg font-semibold">{group.name}</h2>
                                                    </div>
                                                    <div className="relative">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleGroupMenuToggle(group.id)}
                                                            className="h-8 w-8"
                                                        >
                                                            <MoreVertical className="h-4 w-4" />
                                                        </Button>
                                                        <GroupOptionsMenu
                                                            isOpen={groupMenuStates[group.id]}
                                                            onSelect={(action) => handleGroupAction(group.id, action)}
                                                            onClose={() => handleGroupMenuToggle(group.id)}
                                                        />
                                                    </div>
                                                </div>
                                                <Droppable droppableId={group.id.toString()} type="category">
                                                    {(provided) => (
                                                        <div
                                                            className="flex flex-col gap-2 w-full"
                                                            ref={provided.innerRef}
                                                            {...provided.droppableProps}
                                                        >
                                                            {sortCategories(categories).map((category, index) => (
                                                                <Draggable key={category.id} draggableId={category.id.toString()} index={index}>
                                                                    {(provided, snapshot) => (
                                                                        <div
                                                                            ref={provided.innerRef}
                                                                            {...provided.draggableProps}
                                                                            className={(snapshot.isDragging ? 'opacity-80 ' : '') + 'w-full'}
                                                                        >
                                                                            <div className="flex items-center">
                                                                                <span {...provided.dragHandleProps} className="cursor-move mr-2 text-gray-400 hover:text-gray-600">
                                                                                    <GripVertical className="w-5 h-5" />
                                                                                </span>
                                                                                <CategoryCard
                                                                                    category={category}
                                                                                    onEdit={handleEdit}
                                                                                    onDelete={handleDelete}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </Draggable>
                                                            ))}
                                                            {provided.placeholder}
                                                        </div>
                                                    )}
                                                </Droppable>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
                {/* Категории вне группы */}
                {filteredCategoriesWithoutGroup.length > 0 && (
                    <div>
                        <h2 className="text-lg font-semibold mb-2">{t('categories.withoutGroup', 'Без группы')}</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredCategoriesWithoutGroup.map(category => (
                                <CategoryCard
                                    key={category.id}
                                    category={category}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <CreateCategoryModal
                isOpen={isCreateModalOpen}
                onClose={() => {
                    setIsCreateModalOpen(false);
                    setEditingCategory(null);
                }}
                category={editingCategory}
            />
            <CreateGroupModal
                isOpen={isCreateGroupModalOpen}
                onClose={() => setIsCreateGroupModalOpen(false)}
            />
            <EditGroupModal
                isOpen={isEditGroupModalOpen}
                onClose={() => {
                    setIsEditGroupModalOpen(false);
                    setEditingGroup(null);
                }}
                group={editingGroup}
                onSave={handleGroupSave}
            />
            <DeleteGroupModal
                isOpen={isDeleteGroupModalOpen}
                onClose={() => {
                    setIsDeleteGroupModalOpen(false);
                    setDeletingGroup(null);
                }}
                groupName={deletingGroup?.name}
                onDelete={handleGroupDelete}
            />
        </AppHeaderLayout>
    );
}

export default MyCategories; 